import React, { useEffect, useRef, useState } from 'react';
import { useToast } from '@/lib/use-toast';
import p5 from 'p5';
import Editor from '@monaco-editor/react';
import { findErrorLine, setupMonacoWithP5Types, createCustomConsole, createErrorHandlingWrapper, setupAssetPathRedirection } from '@/components/EditorUtils';
import { useTheme } from '@/lib/theme-provider';

// Fonction globale pour nettoyer les sketches précédents
window._cleanupP5Sketches = window._cleanupP5Sketches || {};

// Gestionnaire global des logs pour chaque sketch
window._sketchLogs = window._sketchLogs || {};

const EditorCanvasConsole = ({ 
  initialCode, 
  canvasId, 
  title,
  height = "300px",
  editorCols = 6, // Nombre de colonnes sur 12 occupées par l'éditeur (8/12 par défaut)
  showConsole = false, // Option pour afficher la console au lieu du canvas par défaut
  autoFocus = false, // Option pour donner automatiquement le focus à l'éditeur
  allowCopyPaste = false, // Option pour autoriser le copier-coller
  wordir,
  onChange, // Fonction de callback pour notifier le composant parent des changements
}) => {

  const [code, setCode] = useState(initialCode || '');
  const [logs, setLogs] = useState([]);
  const [displayConsole, setDisplayConsole] = useState(showConsole); // État pour contrôler ce qui est affiché
  const [consoleHighlight, setConsoleHighlight] = useState(false); // État pour contrôler la couleur du titre "Console:"
  const [showToggleButton, setShowToggleButton] = useState(!showConsole); // Cacher le bouton si showConsole est true
  const canvasRef = useRef(null);
  const sketchInstance = useRef(null);
  const sketchIdRef = useRef(`sketch_${canvasId}`);
  const editorRef = useRef(null);
  const monacoRef = useRef(null);
  const errorDecorations = useRef([]);
  const errorRef = useRef(false); // Référence pour suivre si une erreur a été détectée
  const { toast } = useToast();
  const { theme } = useTheme();

  // Mapping des classes Tailwind pour editorCols
  const editorColsClasses = {
    3: "md:w-3/12",
    4: "md:w-4/12", 
    5: "md:w-5/12",
    6: "md:w-6/12",
    7: "md:w-7/12",
    8: "md:w-8/12",
    9: "md:w-9/12",
    10: "md:w-10/12",
    11: "md:w-11/12",
    12: "md:w-full"
  };

  const displayColsClasses = {
    3: "md:w-9/12",
    4: "md:w-8/12",
    5: "md:w-7/12", 
    6: "md:w-6/12",
    7: "md:w-5/12",
    8: "md:w-4/12",
    9: "md:w-3/12",
    10: "md:w-2/12",
    11: "md:w-1/12",
    12: "md:w-0"
  };

  const editorClassName = editorColsClasses[editorCols] || editorColsClasses[8];
  const displayClassName = displayColsClasses[editorCols] || displayColsClasses[8];

  // Charger le code sauvegardé au chargement si storageKey est fourni
  useEffect(() => {
    if (canvasId) {
      const savedCode = localStorage.getItem(canvasId);
      if (savedCode) {
        setCode(savedCode);
      }
    }
  }, [canvasId]);

  // Fonction pour configurer Monaco avec les typages p5.js
  const handleEditorDidMount = (editor, monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;
    
    // Utiliser la fonction partagée pour configurer Monaco
    setupMonacoWithP5Types(editor, monaco);
    
    // Bloquer le copier-coller seulement si allowCopyPaste est false
    if (!allowCopyPaste) {
      editor.addAction({
        id: 'block-paste',
        label: 'Block Paste',
        keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyV],
        run: () => {
          // Afficher un message pédagogique quand l'étudiant essaie de coller
          toast({
            title: "✏️ On est là pour apprendre !",
            description: "Pas de copier-coller ! C'est en tapant le code que tu mémorises le mieux.",
            variant: "default",
            duration: 3000
          });
          return null; // Empêcher l'action par défaut
        }
      });
    }
    
    // Donner le focus à l'éditeur si autoFocus est activé
    if (autoFocus) {
      setTimeout(() => {
        editor.focus();
      }, 500); // Petit délai pour s'assurer que l'éditeur est complètement monté
    }
  };


  // Fonction pour ajouter une décoration d'erreur sur la ligne
  const highlightErrorLine = (lineNumber) => {
    if (!editorRef.current || !monacoRef.current || !lineNumber) return;
    
    // Supprimer les décorations existantes
    if (errorDecorations.current.length > 0) {
      errorDecorations.current = editorRef.current.deltaDecorations(errorDecorations.current, []);
    }
    
    // Ajouter la nouvelle décoration
    errorDecorations.current = editorRef.current.deltaDecorations([], [
      {
        range: new monacoRef.current.Range(lineNumber, 1, lineNumber, 1),
        options: {
          isWholeLine: true,
          className: 'myErrorLine'
        }
      }
    ]);
  };

  // Fonction pour effacer les décorations d'erreur
  const clearErrorHighlights = () => {
    if (editorRef.current && errorDecorations.current.length > 0) {
      errorDecorations.current = editorRef.current.deltaDecorations(errorDecorations.current, []);
    }
  };

  // Fonction pour afficher l'erreur dans la console et le canvas
  const showErrorInConsole = (errorLine, errorMessage, stackTrace) => {
    // Ne pas ajouter de message si une erreur est déjà signalée
    if (errorRef.current) {
      return;
    }
    
    // Marquer qu'une erreur a été signalée
    errorRef.current = true;

    // Mettre en surbrillance la ligne d'erreur dans l'éditeur
    highlightErrorLine(errorLine);

    let message = errorLine 
      ? `Erreur ligne ${errorLine}: ${errorMessage}`
      : `Erreur: ${errorMessage}`;

    console.log(stackTrace);

    // Ajouter l'erreur aux logs spécifiques de ce sketch
    window._sketchLogs[sketchIdRef.current].push(message);
    
    // Mettre à jour les logs dans l'interface
    setLogs([...window._sketchLogs[sketchIdRef.current]]);
    
    // Mettre en évidence le titre de la console
    setConsoleHighlight(true);
    setTimeout(() => {
      setConsoleHighlight(false);
    }, 300);
    
    // Si nous sommes en mode canvas, basculer automatiquement vers la console
    if (!displayConsole) {
      // Optionnel: basculer automatiquement en mode console pour voir l'erreur
      // setDisplayConsole(true);
    }
    
    // Nettoyage du canvas existant
    if (canvasRef.current) {
      // Vérifier une dernière fois qu'il n'y a pas de canvas avant d'en créer un nouveau
      document.querySelectorAll(`#canvas-${sketchIdRef.current} canvas`).forEach(canvas => {
        if (canvas && canvas.parentNode) {
          canvas.parentNode.removeChild(canvas);
        }
      });
    }
    
    // Créer un canvas d'erreur
    const errorSketch = (p) => {
      p.setup = function() {
        p.createCanvas(400, 400);
        p.background(50);
        p.fill(255);
        p.textSize(16);
        p.textAlign(p.CENTER, p.CENTER);
        p.text(message, p.width/2, p.height/2);
      }
      p.draw = function() {} // Fonction vide
      p.noLoop(); // Important: arrêter la boucle draw
    };
    
    // Créer une nouvelle instance p5 pour afficher l'erreur sur le canvas
    setTimeout(() => {
      if (sketchInstance.current) {
        try {
          sketchInstance.current.remove();
        } catch (e) {
          console.error("Erreur lors du nettoyage du sketch:", e);
        }
      }
      
      sketchInstance.current = new p5(errorSketch, canvasRef.current);
      
      // Cacher le canvas d'erreur si on est en mode console
      if (displayConsole) {
        setTimeout(() => {
          if (canvasRef.current) {
            const canvasElement = canvasRef.current.querySelector('canvas');
            if (canvasElement) {
              canvasElement.style.display = 'none';
            }
          }
        }, 0);
      }
    }, 0);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupSketch();
    };
  }, []);

  // Exécuter le sketch au premier rendu
  useEffect(() => {
    // Ajouter un délai pour s'assurer que les éléments DOM sont correctement rendus
    const timer = setTimeout(() => {
      // Ne pas exécuter en mode console par défaut
      if (!showConsole) {
        runSketch();
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, []); // Ne dépend plus de displayConsole, exécuté une seule fois

  // Effet pour surveiller les changements de logs de CE sketch spécifique
  useEffect(() => {
    // Initialiser l'entrée des logs pour ce sketch si elle n'existe pas
    window._sketchLogs[sketchIdRef.current] = window._sketchLogs[sketchIdRef.current] || [];
    
    // Pas besoin d'un interval ici, les logs sont déjà mis à jour directement
    
    return () => {
      // Nettoyage des logs au démontage
      if (window._sketchLogs[sketchIdRef.current]) {
        window._sketchLogs[sketchIdRef.current] = [];
      }
    };
  }, []);

  // Fonction pour nettoyer proprement l'instance p5
  const cleanupSketch = () => {
    const sketchId = sketchIdRef.current;
    
    // Nettoyer l'instance p5 si elle existe
    if (sketchInstance.current) {
      try {
        sketchInstance.current.remove();
      } catch (e) {
        console.log(`Erreur lors du nettoyage du sketch ${sketchId}:`, e);
      }
      sketchInstance.current = null;
    }
    
    // Nettoyer les variables globales liées à ce sketch
    if (window._p5_sketches && window._p5_sketches[sketchId]) {
      delete window._p5_sketches[sketchId];
    }
    
    // Vider le canvas
    if (canvasRef.current) {
      while (canvasRef.current.firstChild) {
        canvasRef.current.removeChild(canvasRef.current.firstChild);
      }
    }
  };

  // Fonction pour basculer entre le mode Canvas et Console
  const toggleDisplay = (e) => {
    e.stopPropagation();
    
    // Simplement basculer l'affichage sans nettoyer ou réexécuter quoi que ce soit
    setDisplayConsole(!displayConsole);
    
    // Si on passe en mode console, on met en pause le sketch pour éviter qu'il continue à s'exécuter
    if (!displayConsole && sketchInstance.current) {
      try {
        // Cacher le canvas sans le détruire
        if (canvasRef.current) {
          const canvasElement = canvasRef.current.querySelector('canvas');
          if (canvasElement) {
            canvasElement.style.display = 'none';
          }
        }
      } catch (e) {
        console.error("Erreur lors de la mise en pause du sketch:", e);
      }
    } else {
      // Si on repasse en mode canvas, réafficher le canvas
      if (canvasRef.current) {
        const canvasElement = canvasRef.current.querySelector('canvas');
        if (canvasElement) {
          canvasElement.style.display = 'block';
        }
      }
    }
  };

  // Gestionnaire de changement de code pour effacer les erreurs et sauvegarder
  const handleCodeChange = (newCode) => {
    setCode(newCode);
    
    // Effacer les décorations d'erreur quand l'utilisateur modifie le code
    clearErrorHighlights();
    
    // Sauvegarder dans localStorage si une clé est fournie
    if (canvasId) {
      localStorage.setItem(canvasId, newCode);
    }
    
    // Appeler le callback onChange du parent s'il existe
    if (onChange) {
      onChange(newCode);
    }
  };

  // Fonction pour réinitialiser les logs
  const resetLogs = () => {
    if (window._sketchLogs[sketchIdRef.current]) {
      window._sketchLogs[sketchIdRef.current] = [];
    }
    setLogs([]);
  };

  // Fonction principale pour exécuter le sketch
  const runSketch = () => {
    cleanupSketch();
    resetLogs(); // Réinitialiser les logs seulement au lancement d'un nouveau sketch
    errorRef.current = false; // Réinitialiser l'état d'erreur
    
    // Réinitialiser les décorations d'erreur précédentes
    clearErrorHighlights();

    const sketchFn = (p) => {
      try {
        const wrappedCode = `${code}\n//# sourceURL=userCode.js`;

        // Créer un objet console personnalisé pour capturer les logs
        const customConsole = createCustomConsole(log => {
          // Ajouter le log aux logs globaux de ce sketch
          if (!window._sketchLogs[sketchIdRef.current]) {
            window._sketchLogs[sketchIdRef.current] = [];
          }
          window._sketchLogs[sketchIdRef.current].push(log);
          
          // Mettre à jour les logs dans l'interface
          setLogs(prevLogs => [...prevLogs, log]);
          
          // Faire clignoter le titre de la console
          setConsoleHighlight(true);
          setTimeout(() => {
            setConsoleHighlight(false);
          }, 300);
        });
        
        // Configurer la redirection des chemins d'assets avant de créer les wrappers
        setupAssetPathRedirection(p,   wordir, (errorLine, errorMessage, stack) => {
          showErrorInConsole(errorLine, errorMessage, stack);
        });
        
        // Analyser le code utilisateur
        try {
          // Envelopper le code utilisateur dans une fonction
          // qui s'exécute dans le contexte de l'environnement du sketch
          const userFn = new Function('p', 'console', `
            with(p) {
              // Définir print comme alias de console.log
              const print = (...args) => console.log(...args);
              ${wrappedCode}
              return {
                setup: typeof setup === 'function' ? setup : null,
                draw: typeof draw === 'function' ? draw : null,
                mousePressed: typeof mousePressed === 'function' ? mousePressed : null,
                mouseReleased: typeof mouseReleased === 'function' ? mouseReleased : null,
                mouseMoved: typeof mouseMoved === 'function' ? mouseMoved : null,
                keyPressed: typeof keyPressed === 'function' ? keyPressed : null,
                keyReleased: typeof keyReleased === 'function' ? keyReleased : null
              };
            }
          `);
          
          // Exécuter le code et récupérer les fonctions utilisateur
          const userFunctions = userFn(p, customConsole);

          // Callback d'erreur pour les wrappers
          const errorCallback = (errorLine, errorMessage, stack) => {
            showErrorInConsole(errorLine, errorMessage, stack);
          };

          // Appliquer les wrappers aux fonctions p5.js avec la fonction partagée
          p.setup = createErrorHandlingWrapper(
            function() {
              if (userFunctions.setup) {
                userFunctions.setup.call(customConsole);
              } else {
                p.createCanvas(300, 300);
                p.background(220);
              }
              
              // Si on est en mode console, cacher le canvas après l'avoir créé
              if (displayConsole) {
                setTimeout(() => {
                  if (canvasRef.current) {
                    const canvasElement = canvasRef.current.querySelector('canvas');
                    if (canvasElement) {
                      canvasElement.style.display = 'none';
                    }
                  }
                }, 0);
              }
            }, 
            "setup",
            errorCallback
          );
          
          p.draw = createErrorHandlingWrapper(
            function() {
              if (userFunctions.draw) {
                userFunctions.draw.call(customConsole);
              }
            }, 
            "draw", 
            errorCallback,
            true // Arrêter la loop en cas d'erreur
          );
          
          // Connecter les événements avec le wrapper d'erreur partagé
          if (userFunctions.mousePressed) {
            p.mousePressed = createErrorHandlingWrapper(
              () => userFunctions.mousePressed.call(customConsole),
              "mousePressed",
              errorCallback
            );
          }
          
          if (userFunctions.mouseReleased) {
            p.mouseReleased = createErrorHandlingWrapper(
              () => userFunctions.mouseReleased.call(customConsole),
              "mouseReleased",
              errorCallback
            );
          }
          
          if (userFunctions.mouseMoved) {
            p.mouseMoved = createErrorHandlingWrapper(
              () => userFunctions.mouseMoved.call(customConsole),
              "mouseMoved",
              errorCallback
            );
          }
          
          if (userFunctions.keyPressed) {
            p.keyPressed = createErrorHandlingWrapper(
              () => userFunctions.keyPressed.call(customConsole),
              "keyPressed",
              errorCallback
            );
          }
          
          if (userFunctions.keyReleased) {
            p.keyReleased = createErrorHandlingWrapper(
              () => userFunctions.keyReleased.call(customConsole),
              "keyReleased",
              errorCallback
            );
          }
          
        } catch (error) {
          console.error(`Erreur de syntaxe (${sketchIdRef.current}):`, error);
          
          // Utiliser findErrorLine pour trouver la ligne d'erreur
          const errorLine = findErrorLine(code);
          
          // Afficher l'erreur avec la fonction partagée
          showErrorInConsole(errorLine, error.message, error.stack);
        }
      } catch (error) {
        console.error(`Erreur lors de l'initialisation de p5 (${sketchIdRef.current}):`, error);
        
        // Afficher l'erreur avec la fonction partagée
        showErrorInConsole(null, error.message, error.stack);
        
        toast({
          title: "Erreur d'exécution",
          description: error.message,
          variant: "destructive"
        });
      }
    };
    
    // Attendre le prochain cycle pour s'assurer que le DOM est nettoyé
    setTimeout(() => {
      try {
        // Créer l'instance p5 normalement, quel que soit le mode
        sketchInstance.current = new p5(sketchFn, canvasRef.current);
      } catch (error) {
        console.error(`Erreur lors de l'initialisation de p5 (${sketchIdRef.current}):`, error);
        
        // Afficher l'erreur avec la fonction partagée
        showErrorInConsole(null, error.message, error.stack);
        
        toast({
          title: "Erreur d'exécution",
          description: error.message,
          variant: "destructive"
        });
      }
    }, 50);
  };

  return (
    <div className="flex flex-col md:flex-row gap-4">
      {/* Éditeur */}
      <div className={`w-full ${editorClassName} flex flex-col space-y-4 relative`}>
        <div className="rounded-lg overflow-hidden relative">
          <Editor
            height={height}
            defaultLanguage="javascript"
            value={code}
            onChange={handleCodeChange}
            onMount={handleEditorDidMount}
            theme={theme === 'dark' ? "vs-dark" : "vs"}
            options={{
              minimap: { enabled: false },
              fontSize: 16,
              fontFamily: "monospace",
              wordWrap: 'on',
              tabSize: 2,
              scrollBeyondLastLine: false,
              lineNumbers: 'on',
              folding: false,
              autoIndent: true,
              suggestOnTriggerCharacters: false,
              overviewRulerLanes: 0,
              hideCursorInOverviewRuler: true,
              overviewRulerBorder: false,
              selectionHighlight: false,
              occurrencesHighlight: false,
              matchBrackets: 'never',
              renderLineHighlight: 'none',
              contextmenu: false, // Désactiver le menu contextuel (clic droit)
              stickyScroll: {
                enabled: false
              }
            }}
          />
          
          {/* Boutons de contrôle */}
          <div className="absolute top-3 right-3 z-30 flex flex-col gap-2">
            {/* Bouton Play */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                // Toujours exécuter le code sans changer le mode d'affichage
                runSketch();
              }}
              className="bg-blue-500 hover:bg-blue-600 text-white rounded-full w-10 h-10 flex items-center justify-center shadow-md"
              title="Exécuter le code"
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                viewBox="0 0 24 24" 
                fill="currentColor" 
                className="w-5 h-5 ml-0.5"
              >
                <path 
                  d="M8 5.14v14l11-7-11-7z" 
                />
              </svg>
            </button>
            
            {/* Bouton Toggle Console/Canvas - affiché seulement si showToggleButton est true */}
            {showToggleButton && (
              <button
                onClick={toggleDisplay}
                className={`${displayConsole ? 'bg-green-500 hover:bg-green-600' : 'bg-purple-500 hover:bg-purple-600'} text-white rounded-full w-10 h-10 flex items-center justify-center shadow-md`}
                title={displayConsole ? "Afficher le canvas" : "Afficher la console"}
              >
                {displayConsole ? (
                  // Icône Canvas (rectangle)
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    viewBox="0 0 24 24" 
                    fill="currentColor" 
                    className="w-5 h-5"
                  >
                    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14z" />
                  </svg>
                ) : (
                  // Icône Console (terminal)
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    viewBox="0 0 24 24" 
                    fill="currentColor" 
                    className="w-5 h-5"
                  >
                    <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V8h16v10z" />
                    <path d="M6 10h2v2H6zm0 4h8v2H6z" />
                  </svg>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
      
      {/* Zone d'affichage: Canvas OU Console */}
      <div className={`w-full ${displayClassName} flex flex-col`}>
        {/* Le Canvas - toujours rendu mais peut être caché */}
        <div 
          ref={canvasRef} 
          className="canvas-container bg-neutral-100 dark:bg-neutral-800 w-full flex items-center justify-center rounded-lg"
          style={{ 
            height: height,
            display: displayConsole ? 'none' : 'flex',
            border: 'none', // Assurer qu'il n'y a pas de bordure
            outline: 'none'  // Assurer qu'il n'y a pas d'outline
          }}
          aria-label={`Canvas interactif ${canvasId}: ${title}`}
          id={`canvas-${sketchIdRef.current}`}
        />
        
        {/* La Console - rendue uniquement en mode console */}
        {displayConsole && (
          <div 
            className={`log-container ${theme === 'dark' ? 'bg-gray-900 text-white' : 'bg-neutral-100 text-gray-800'} w-full rounded-lg overflow-auto p-3 font-mono text-sm`}
            style={{ 
              height: height,
              border: 'none' // Assurer qu'il n'y a pas de bordure
            }}
            ref={(el) => {
              // Faire défiler automatiquement vers le bas lorsqu'il y a de nouveaux logs
              if (el) {
                el.scrollTop = el.scrollHeight;
              }
            }}
          >
            <div className={`font-bold mb-2 ${consoleHighlight ? 'text-yellow-400' : theme === 'dark' ? 'text-white' : 'text-gray-800'} transition-colors duration-300`}>Console:</div>
            {logs.map((log, index) => (
              <div key={index} className="log-line">
                {log}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EditorCanvasConsole; 